import { useState, useEffect } from 'react';

interface PropertyInputProps {
  label: string;
  value: string | number | undefined;
  type?: 'text' | 'number' | 'color';
  step?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  onChange: (value: string | number | undefined) => void;
}

export function PropertyInput({ label, value, type = 'text', step, placeholder, min, max, onChange }: PropertyInputProps) {
  // Use local state for the input value to allow free editing
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '');
  const [isFocused, setIsFocused] = useState(false);

  // Sync local value with prop when not focused
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value?.toString() ?? '');
    }
  }, [value, isFocused]);

  return (
    <div className="property-row">
      <label>{label}</label>
      <input
        type={type}
        value={localValue}
        step={step}
        placeholder={placeholder}
        onFocus={() => setIsFocused(true)}
        onChange={(e) => {
          setLocalValue(e.target.value);
          if (type === 'number') {
            const parsed = parseFloat(e.target.value);
            if (!isNaN(parsed)) {
              onChange(parsed);
            }
            // Don't call onChange for empty/invalid - wait for blur
          } else {
            onChange(e.target.value);
          }
        }}
        onBlur={(e) => {
          setIsFocused(false);
          // Clamp to min/max on blur for number inputs
          if (type === 'number') {
            const parsed = parseFloat(e.target.value);
            if (!isNaN(parsed)) {
              let clamped = parsed;
              if (min !== undefined) clamped = Math.max(min, clamped);
              if (max !== undefined) clamped = Math.min(max, clamped);
              onChange(clamped);
              setLocalValue(clamped.toString());
            } else if (min !== undefined) {
              // If empty/invalid on blur, reset to min
              onChange(min);
              setLocalValue(min.toString());
            }
          }
        }}
      />
    </div>
  );
}
